type Props = {
    value: string; // '0'〜'9' or ' '（空白）
    width?: number; // 1桁の幅
    height?: number; // 1桁の高さ
    thickness?: number; // セグメントの太さ
    on?: string; // ON色
    off?: string; // OFF色
};

const MAP: Record<string, (keyof Seg)[]> = {
    '0': ['a', 'b', 'c', 'd', 'e', 'f'],
    '1': ['b', 'c'],
    '2': ['a', 'b', 'g', 'e', 'd'],
    '3': ['a', 'b', 'g', 'c', 'd'],
    '4': ['f', 'g', 'b', 'c'],
    '5': ['a', 'f', 'g', 'c', 'd'],
    '6': ['a', 'f', 'g', 'e', 'c', 'd'],
    '7': ['a', 'b', 'c'],
    '8': ['a', 'b', 'c', 'd', 'e', 'f', 'g'],
    '9': ['a', 'b', 'c', 'd', 'f', 'g'],
    '-': ['g'], // マイナス記号は中央の横棒のみ
    ' ': [],
};

type Seg = {
    a: [number, number, number, number];
    b: [number, number, number, number];
    c: [number, number, number, number];
    d: [number, number, number, number];
    e: [number, number, number, number];
    f: [number, number, number, number];
    g: [number, number, number, number];
};

/**
 * 7セグメント表示の数字を表示する
 */
export default function SevenSegDigit({
    value,
    width = 140,
    height = 240,
    thickness = 18,
    on = '#111111',
    off = '#e5e7eb',
}: Props) {
    const s = thickness;
    const w = width;
    const h = height;
    const taper = s * 0.5; // ダイアモンド型の先端の長さ

    // 各セグメントの[x, y, w, h]（基準位置）
    // 縦の4つを離し、横棒の3つを長くして近づける
    const verticalGap = s * 0.2; // 縦セグメントと横セグメントの間隔
    const verticalExtension = s * 0.4; // 縦棒を長くする係数
    const horizontalExtension = s * 0.5; // 横棒を長くする係数（小さくして横棒を短く）
    const horizontalGap = s * 0.2; // 横棒同士の間隔を縮める係数
    const seg: Seg = {
        // a: 上段の横棒 - 長くしてgに近づける少し位置を下げる
        a: [s - horizontalExtension / 2, s * 0.2, w - 2 * s + horizontalExtension, s],
        // b: 右上の垂直セグメント - 長くする
        b: [w - s, s, s, h / 2 - 1.5 * s - verticalGap + verticalExtension],
        // c: 右下の垂直セグメント - 長くする
        c: [
            w - s,
            h / 2 + 0.5 * s + verticalGap - verticalExtension / 2,
            s,
            h / 2 - 1.5 * s - verticalGap + verticalExtension,
        ],
        // d: 下段の横棒 - 長くしてgに近づける
        d: [s - horizontalExtension / 2, h - s, w - 2 * s + horizontalExtension, s],
        // e: 左下の垂直セグメント - 長くする
        e: [
            0,
            h / 2 + 0.5 * s + verticalGap - verticalExtension / 2,
            s,
            h / 2 - 1.5 * s - verticalGap + verticalExtension,
        ],
        // f: 左上の垂直セグメント - 長くする
        f: [0, s, s, h / 2 - 1.5 * s - verticalGap + verticalExtension],
        // g: 中央の横棒 - 長くしてaとdに近づける、位置を少し下に
        g: [
            s - horizontalExtension / 2,
            h / 2 - s / 2 - horizontalGap + s * 0.3,
            w - 2 * s + horizontalExtension,
            s,
        ],
    };

    // 各セグメントの座標（ダイアモンド型）
    const getSegPoints = (k: keyof Seg): string => {
        const [x, y, ww, hh] = seg[k];

        if (k === 'a' || k === 'd' || k === 'g') {
            // 水平セグメント（左右が尖る）
            return `${x + taper},${y} ${x + ww - taper},${y} ${x + ww},${y + hh / 2} ${x + ww - taper},${y + hh} ${x + taper},${y + hh} ${x},${y + hh / 2}`;
        } else {
            // 垂直セグメント（上下が尖る）
            return `${x + ww / 2},${y} ${x + ww},${y + taper} ${x + ww},${y + hh - taper} ${x + ww / 2},${y + hh} ${x},${y + hh - taper} ${x},${y + taper}`;
        }
    };

    const onKeys = new Set(MAP[value] ?? []);
    const draw = (k: keyof Seg, i: number) => {
        const points = getSegPoints(k);
        return <polygon key={i} points={points} fill={onKeys.has(k) ? on : off} />;
    };

    // レスポンシブ対応: viewBoxを使用してスケーリング可能にする
    return (
        <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ maxWidth: `${w}px`, maxHeight: `${h}px` }}
            aria-label={value}
        >
            {(['a', 'b', 'c', 'd', 'e', 'f', 'g'] as (keyof Seg)[]).map((k, i) => draw(k, i))}
        </svg>
    );
}
